import { useEffect } from 'react';
import { useRouter } from 'next/router';

import {
  Box,
  FieldSet,
  FormHelp,
  Heading,
  Label,
  LabeledControl,
  Legend,
  Paragraph,
  Popover,
  Radio,
  TextButton,
} from '@zillow/constellation-10';

import { useRentalManagerUserPropertyEditWebApiHandler_isRentersInsuranceRequired } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useFormMode } from '../../../contexts/FormModeContext';
import {
  useDebouncedMultiplexSave,
  useIsOnReviewStep,
  useMultiplexBuildingSessionId,
  useMultiplexDetails,
  useMultiplexListingFlow,
  useMultiplexUpdate,
} from '../../../hooks/queries/multiplex';
import { useGetListingDetails } from '../../../hooks/queries/properties';
import { useFieldSave } from '../../../hooks/useFieldSave';
import { usePropertyEditMutation } from '../../../hooks/usePropertyEditMutation';

import { TRIGGER_LOCATIONS } from '../../../constants/ClickstreamConstants';

import {
  emitRentersInsuranceClickClearEvent,
  emitRentersInsuranceClickNoEvent,
  emitRentersInsuranceClickYesEvent,
  emitRentersInsuranceInfoPopoverEvent,
  emitRentersInsuranceViewContentEvent,
  EVENT_IDS,
} from '../../../analytics/events/rentersInsurance';
import { useAuth } from '../../AuthProvider';

import type { UserPropertyMultiplexUpdateRequestWebDto } from '@zg-rentals/ts-rental-manager-api-client';

const inputName = 'isRentersInsuranceRequired';

const toFormValue = (value: boolean | null | undefined) => (value != null ? String(value) : '');

const toApiBooleanOrNull = (value: string): boolean | null => (value === '' ? null : value === 'true');

const toMultiplexPostData = (value: string): UserPropertyMultiplexUpdateRequestWebDto =>
  ({
    isRentersInsuranceRequired: toApiBooleanOrNull(value),
  }) as unknown as UserPropertyMultiplexUpdateRequestWebDto;

const RentersInsuranceFormField = () => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;

  const isOnReviewStep = useIsOnReviewStep();
  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { data: multiplexData } = useMultiplexDetails({
    enabled: !isOnReviewStep,
  });
  const { isInMultiplexListingFlow } = useMultiplexListingFlow();
  const { multiplexBuildingSessionId } = useMultiplexBuildingSessionId();
  const { user } = useAuth();
  const { mode } = useFormMode();

  const rentersInsurance =
    !isInMultiplexListingFlow || isOnReviewStep
      ? listingDetails?.rentersInsurance
      : (multiplexData?.rentersInsurance ?? listingDetails?.rentersInsurance);

  const isRentersInsuranceRequired = rentersInsurance?.isRentersInsuranceRequired;

  const initialValue = toFormValue(isRentersInsuranceRequired);

  const triggerLocation = mode === 'modal' ? TRIGGER_LOCATIONS.POSTING_PATH_REVIEW : TRIGGER_LOCATIONS.POSTING_PATH;

  const updateMutation = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_isRentersInsuranceRequired,
  });

  const { mutateAsync: multiplexUpdateAsync } = useMultiplexUpdate({
    meta: {
      context: {
        api: 'useMultiplexUpdate',
        details: 'RentersInsuranceFormField',
        fieldName: inputName,
      },
      invalidates: ['listingDetails', 'listingRecommendations'],
    },
  });

  const field = useFieldSave({
    fieldName: inputName,
    initialValue,
    mutation: updateMutation,
    buildMutationVariables: (value) => ({
      getParams: {
        propertyId: listingAlias,
        // @ts-expect-error BE expects boolean, but null is valid for clearing
        isRentersInsuranceRequired: toApiBooleanOrNull(value),
      },
    }),
    autoSaveOnChange: isOnReviewStep || !isInMultiplexListingFlow,
  });

  const { saveDebounced: saveMultiplexDebounced, error: multiplexError } = useDebouncedMultiplexSave({
    multiplexBuildingSessionId,
    multiplexUpdateAsync,
    buildPostData: toMultiplexPostData,
    getUnitErrorOptions: () => ({
      fieldName: inputName,
      fallbackMessage: 'Unable to save renters insurance, refresh and try again',
    }),
    contextDetails: 'RentersInsuranceFormField',
  });

  const handleValueChange = (value: string) => {
    field.onChange(value);

    if (isOnReviewStep || !isInMultiplexListingFlow) {
      return;
    }

    saveMultiplexDebounced(value);
  };

  const displayError = multiplexError ?? field.error;

  useEffect(() => {
    if (rentersInsurance?.isEligibleForIsRentersInsuranceRequiredQuestion && listingDetails && user) {
      emitRentersInsuranceViewContentEvent({ currentProperty: listingDetails, user, triggerLocation });
    }
  }, [rentersInsurance?.isEligibleForIsRentersInsuranceRequiredQuestion, listingDetails, user, triggerLocation]);

  if (!rentersInsurance?.isEligibleForIsRentersInsuranceRequiredQuestion) {
    return null;
  }

  return (
    <div data-testid="RentersInsuranceFormField">
      <FieldSet
        legend={
          <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Legend>Do you require tenants to obtain renters insurance?</Legend>
            <Popover
              tone="info"
              placement="top"
              onOpenChange={(open) => {
                if (open && listingDetails && user) {
                  emitRentersInsuranceInfoPopoverEvent({ currentProperty: listingDetails, user, triggerLocation });
                }
              }}
              header={<Heading level={2}>Renters insurance</Heading>}
              body={
                <Paragraph>
                  Requiring your tenants to have renters insurance could reduce your out-of-pocket expenses in the event
                  of an accident. Without renters insurance, you could be liable for the cost of items stolen or damaged
                  in a disaster. Your answer won’t be displayed on your listing.
                </Paragraph>
              }
              trigger={<Popover.Trigger css={{ marginLeft: '100' }} data-eventid={EVENT_IDS.INFO_POPOVER} />}
            />
          </Box>
        }
        error={!!displayError}
        description={<FormHelp>{displayError}</FormHelp>}
      >
        <LabeledControl
          controlId="renters-insurance-yes"
          label={<Label>Yes, I require renters insurance</Label>}
          control={
            <Radio
              name={inputName}
              value="true"
              checked={field.value === 'true'}
              data-eventid={EVENT_IDS.CLICK_YES}
              onChange={(e) => {
                handleValueChange(e.target.value);
                if (listingDetails && user) {
                  emitRentersInsuranceClickYesEvent({ currentProperty: listingDetails, user, triggerLocation });
                }
              }}
            />
          }
        />
        <LabeledControl
          controlId="renters-insurance-no"
          label={<Label>No, I do not require renters insurance</Label>}
          control={
            <Radio
              name={inputName}
              value="false"
              checked={field.value === 'false'}
              data-eventid={EVENT_IDS.CLICK_NO}
              onChange={(e) => {
                handleValueChange(e.target.value);
                if (listingDetails && user) {
                  emitRentersInsuranceClickNoEvent({ currentProperty: listingDetails, user, triggerLocation });
                }
              }}
            />
          }
        />

        {field.value !== '' && (
          <Box css={{ marginTop: '200', marginLeft: '200' }}>
            <TextButton
              data-eventid={EVENT_IDS.CLICK_CLEAR}
              onClick={() => {
                handleValueChange('');
                if (listingDetails && user) {
                  emitRentersInsuranceClickClearEvent({ currentProperty: listingDetails, user, triggerLocation });
                }
              }}
            >
              Clear
            </TextButton>
          </Box>
        )}
      </FieldSet>
    </div>
  );
};

export default RentersInsuranceFormField;
